import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Search, Star, ChevronDown, 
  Briefcase, Tag, Calendar, Home, Car, Newspaper, Users, Award, Sparkles, DollarSign,
  Crown, Trophy, Mail, Tv
} from "lucide-react";
import { Button } from "@/components/ui/button";

const horseLogo = "/assets/localbest_transparent_v2.png";

export const Navigation = ({ onOpenCreateListing }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [aiQuery, setAiQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const submitAiSearch = (e) => {
    e.preventDefault();
    const q = aiQuery.trim();
    if (!q) return;
    setAiQuery("");
    setIsMobileMenuOpen(false);
    navigate(`/ai-search?q=${encodeURIComponent(q)}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { name: "Search Directory", path: "/directory", icon: Search },
    { name: "Grow My Business", path: "/get-funded", icon: DollarSign },
    { name: "TV Ads Platform", path: "/tv-ads-platform", icon: Tv },
    { name: "Postcard That Talks Back", path: "/postcard-that-talks-back", icon: Mail },
  ];

  // Simplified nav for main bar (with dropdown for Marketplace)
  const mainNavLinks = [
    { name: "Search Directory", path: "/directory", icon: Search },
    { 
      name: "OnlyBest Creators", 
      path: "/onlybest",
      icon: Crown,
      hasDropdown: true,
      dropdown: [
        { name: "Join OnlyBest", path: "/onlybest", icon: Crown },
        { name: "Leaderboard", path: "/leaderboard", icon: Trophy },
        { name: "Meet the Movement", path: "/creators", icon: Users },
      ]
    },
    { 
      name: "Grow My Business", 
      path: "/get-funded",
      icon: Award,
      hasDropdown: true,
      dropdown: [
        { name: "Grow My Business", path: "/get-funded", icon: Award },
        { name: "TV Ads Platform", path: "/tv-ads-platform", icon: Tv },
        { name: "Postcard That Talks Back™", path: "/postcard-that-talks-back", icon: Mail },
        { name: "Get AI Horse-Powered", path: "https://aihpower.com", icon: Sparkles, external: true },
      ]
    },
  ];

  const isActive = (path) => {
    if (path.includes("?")) {
      return location.pathname + location.search === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#020817]/90 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="nav-logo-link">
            <img 
              src={horseLogo} 
              alt="LocalBest.ai" 
              className="h-12 sm:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5 ml-8" ref={dropdownRef}>
            {mainNavLinks.map((link) => (
              <div key={link.path} className="relative">
                {link.hasDropdown ? (
                  <>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === link.name ? null : link.name)}
                      className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors relative ${
                        isActive(link.path) || openDropdown === link.name
                          ? "text-white"
                          : "text-[#94A3B8] hover:text-white"
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === link.name ? "rotate-180" : ""}`} />
                    </button>
                    
                    <AnimatePresence>
                      {openDropdown === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full left-0 mt-2 w-56 py-2 bg-[#0F172A] border border-white/10 rounded-xl shadow-xl"
                        >
                          {link.dropdown.map((item) => (
                            item.external ? (
                              <a
                                key={item.path}
                                href={item.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-[#94A3B8] hover:text-white hover:bg-white/5"
                                data-testid="aih-power-link"
                              >
                                <item.icon className="w-4 h-4" />
                                {item.name}
                              </a>
                            ) : (
                              <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                  isActive(item.path)
                                    ? "text-white bg-[#7C3AED]/20"
                                    : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                                }`}
                              >
                                <item.icon className="w-4 h-4" />
                                {item.name}
                              </Link>
                            )
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`text-sm font-medium whitespace-nowrap transition-colors relative ${
                      isActive(link.path)
                        ? "text-white"
                        : "text-[#94A3B8] hover:text-white"
                    }`}
                  >
                    {link.name}
                    {isActive(link.path) && (
                      <motion.div
                        layoutId="nav-indicator"
                        className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#7C3AED] to-[#00E5FF]"
                      />
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <form onSubmit={submitAiSearch} className="relative">
              <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#00E5FF] pointer-events-none" />
              <input
                type="text"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder="Ask LocalBest AI..."
                className="w-36 focus:w-56 pl-9 pr-3 py-2 rounded-full bg-white/5 border border-white/15 text-white text-sm placeholder:text-[#64748B] focus:border-[#7C3AED] focus:outline-none transition-all duration-300"
                data-testid="nav-ai-search-input"
              />
            </form>
            <Link 
              to="/auth/login"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20 transition-all text-sm font-medium whitespace-nowrap"
              data-testid="dashboard-login-link"
            >
              Dashboard Login
            </Link>
            <Button
              onClick={onOpenCreateListing}
              className="bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#8B5CF6] hover:to-[#7C3AED] text-white rounded-full px-6 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
              data-testid="list-business-btn"
            >
              Create My Free Listing
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#020817]/95 backdrop-blur-xl border-b border-white/5 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-4 py-6 space-y-2">
              <form onSubmit={submitAiSearch} className="relative mb-3">
                <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00E5FF] pointer-events-none" />
                <input
                  type="text"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  placeholder="Ask LocalBest AI... 'roofer in Chicago'"
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white text-sm placeholder:text-[#64748B] focus:border-[#7C3AED] focus:outline-none transition-all"
                  data-testid="nav-mobile-ai-search-input"
                />
              </form>
              {navLinks.map((link) => (
                <Link
                  key={link.path + link.name}
                  to={link.path}
                  className={`flex items-center gap-3 py-3 px-4 rounded-lg text-base font-medium ${
                    isActive(link.path)
                      ? "text-white bg-[#7C3AED]/20"
                      : "text-[#94A3B8] hover:bg-white/5"
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/10 space-y-3">
                <Link 
                  to="/auth/login"
                  className="flex items-center justify-center gap-2 py-3 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981]"
                >
                  Dashboard Login
                </Link>
                <a 
                  href="https://aihpower.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-2 text-sm text-[#94A3B8] hover:text-white"
                >
                  Get AI Horse-Powered
                </a>
                <Button 
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onOpenCreateListing();
                  }}
                  className="w-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white rounded-full"
                >
                  Create My Free Listing
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navigation;
